import { useEffect, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useLang } from "../components/Layout";
import { Reveal, Label, GoldLink, OutlineLink, Diamond } from "../components/Reveal";
import Marquee from "../components/Marquee";
import { HERO_IMG, FOUNDER_IMG } from "../i18n";

const Hero = ({ lang, t }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative flex min-h-screen items-center overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        <img src={HERO_IMG} alt="" className="h-[120%] w-full object-cover object-[70%_30%] opacity-90" />
        <div className="absolute inset-0 bg-gradient-to-b from-brand-ink/50 via-transparent to-brand-ink" />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-ink/80 via-brand-ink/30 to-transparent" />
      </motion.div>

      <motion.div
        aria-hidden="true"
        animate={{ rotate: 360 }}
        transition={{ duration: 140, repeat: Infinity, ease: "linear" }}
        className="absolute -right-40 top-1/2 h-[560px] w-[560px] -translate-y-1/2 rounded-full border border-brand-gold/15"
      >
        <div className="absolute inset-10 rounded-full border border-brand-gold/10" />
        <div className="absolute inset-24 rounded-full border border-dashed border-brand-gold/20" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-[1400px] px-6 md:px-10 pt-32 pb-20">
        <motion.p
          data-testid="hero-eyebrow"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="uppercase tracking-luxe text-[11px] font-medium text-brand-gold"
        >
          {t.hero.eyebrow}
        </motion.p>

        <h1 data-testid="hero-title" className="mt-8 font-serif text-4xl sm:text-6xl lg:text-7xl xl:text-8xl font-light leading-[1.05] text-white tracking-tight max-w-5xl">
          {t.hero.titleLines.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1.1, delay: 0.35 + i * 0.15, ease: [0.22, 1, 0.36, 1] }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          data-testid="hero-subtitle"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.9 }}
          className="mt-8 max-w-2xl text-base md:text-lg font-light leading-relaxed text-white/60"
        >
          {t.hero.sub}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.1 }}
          className="mt-12 flex flex-col gap-4 sm:flex-row"
        >
          <GoldLink testId="hero-cta-diagnostic" to={`/${lang}/bilan`}>
            {t.hero.ctaPrimary} <ArrowRight size={16} />
          </GoldLink>
          <OutlineLink testId="hero-cta-method" to={`/${lang}/services`}>
            {t.hero.ctaSecondary}
          </OutlineLink>
        </motion.div>
      </motion.div>
    </section>
  );
};

const Method = ({ t }) => (
  <section data-testid="method-section" className="border-t border-white/10">
    <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-24 md:py-32">
      <Reveal>
        <Label testId="method-label">{t.method.label}</Label>
        <h2 className="mt-6 max-w-3xl font-serif text-3xl sm:text-5xl font-light leading-tight text-white">
          {t.method.title}
        </h2>
      </Reveal>
      <Reveal delay={0.15}>
        <blockquote
          data-testid="method-quote"
          className="mt-12 max-w-3xl border-l-2 border-brand-gold pl-6 md:pl-10 font-serif text-xl md:text-2xl font-light italic leading-relaxed text-white/75"
        >
          {t.method.quote}
        </blockquote>
      </Reveal>
      <div className="mt-16 grid gap-6 md:grid-cols-3">
        {t.method.cards.map((c, i) => (
          <Reveal key={c.num} delay={i * 0.12}>
            <div
              data-testid={`method-card-${c.num}`}
              className="group h-full border border-white/10 bg-brand-surface p-8 md:p-10 transition-all duration-300 hover:border-brand-gold/50 hover:shadow-[0_8px_32px_rgba(201,169,110,0.1)] hover:-translate-y-1"
            >
              <div className="flex items-baseline justify-between">
                <span className="font-serif text-4xl font-light text-brand-gold/40">{c.num}</span>
                <span className="uppercase tracking-luxe text-[10px] text-brand-gold">{c.tag}</span>
              </div>
              <h3 className="mt-8 font-serif text-2xl font-light text-white">
                {c.name} <span className="text-white/30">— {c.title}</span>
              </h3>
              <p className="mt-4 text-sm font-light leading-relaxed text-white/55">{c.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Pathways = ({ lang, t }) => (
  <section data-testid="pathways-section" className="border-t border-white/10 bg-brand-surface/40">
    <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-24 md:py-32">
      <Reveal>
        <Label testId="pathways-label">{t.pathways.label}</Label>
        <h2 className="mt-6 max-w-3xl font-serif text-3xl sm:text-5xl font-light leading-tight text-white">
          {t.pathways.title}
        </h2>
      </Reveal>
      <div className="mt-16 grid gap-6 sm:grid-cols-2 xl:grid-cols-4">
        {t.pathways.offers.map((o, i) => (
          <Reveal key={o.id} delay={i * 0.1} className="h-full">
            <div
              data-testid={`offer-card-${o.id}`}
              className={`relative flex h-full flex-col border bg-brand-ink p-8 transition-all duration-300 hover:-translate-y-1 ${
                o.badge
                  ? "border-brand-gold/60 shadow-[0_0_40px_rgba(201,169,110,0.12)]"
                  : "border-white/10 hover:border-brand-gold/50"
              }`}
            >
              {o.badge && (
                <span className="absolute -top-3 left-8 bg-brand-gold px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.15em] text-brand-ink">
                  {o.badge}
                </span>
              )}
              <h3 className="font-serif text-xl font-light text-white">{o.name}</h3>
              <p className="mt-4 font-serif text-4xl font-light text-brand-gold">{o.price}</p>
              <ul className="mt-6 flex-1 space-y-3">
                {o.features.map((f, j) => (
                  <li key={j} className="flex items-start gap-3 text-sm font-light text-white/55">
                    <Diamond className="mt-1 shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              <GoldLink testId={`offer-cta-${o.id}`} to={`/${lang}/bilan`} className="mt-8 w-full !px-4 !py-3 text-xs">
                {o.cta}
              </GoldLink>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const KitHighlight = ({ lang, t }) => (
  <section data-testid="kit-highlight-section" className="border-t border-white/10">
    <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-24 md:py-32">
      <Reveal>
        <div className="relative overflow-hidden border border-brand-gold/30 bg-gradient-to-b from-brand-elevated to-brand-ink p-10 md:p-16">
          <div className="absolute -right-24 -top-24 h-72 w-72 rounded-full border border-brand-gold/15" />
          <div className="absolute -right-10 -top-10 h-72 w-72 rounded-full border border-brand-gold/10" />
          <Label testId="kit-home-label">{t.kitHome.label}</Label>
          <h2 className="mt-6 max-w-3xl font-serif text-3xl sm:text-5xl font-light leading-tight text-white">
            {t.kitHome.title}
          </h2>
          <p className="mt-6 max-w-2xl text-base font-light leading-relaxed text-white/60">{t.kitHome.desc}</p>
          <div className="mt-10 flex flex-wrap items-center gap-8">
            <GoldLink testId="kit-home-cta" to={`/${lang}/kit-digital-premium`}>
              {t.kitHome.cta} <ArrowRight size={16} />
            </GoldLink>
            <span data-testid="kit-home-price" className="font-serif text-4xl font-light text-brand-gold">{t.kitHome.price}</span>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

const Founder = ({ lang, t }) => (
  <section data-testid="founder-section" className="border-t border-white/10 bg-brand-surface/40">
    <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-24 md:py-32">
      <div className="grid items-center gap-12 lg:grid-cols-2">
        <Reveal>
          <div className="relative mx-auto max-w-md overflow-hidden border border-brand-gold/30 bg-gradient-to-b from-brand-elevated to-brand-ink p-2 shadow-2xl">
            <img
              data-testid="founder-photo"
              src={FOUNDER_IMG}
              alt="Rodrigue Cassildé"
              className="aspect-[4/5] w-full object-cover grayscale-[20%]"
            />
            <div className="pointer-events-none absolute inset-2 bg-gradient-to-t from-brand-ink/60 via-transparent to-transparent" />
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <Label testId="founder-label">{t.founder.label}</Label>
          <h2 className="mt-6 font-serif text-4xl sm:text-5xl font-light text-white">{t.founder.name}</h2>
          <p className="mt-6 max-w-xl text-base font-light leading-relaxed text-white/60">{t.founder.bio}</p>
          <OutlineLink testId="founder-cta" to={`/${lang}/a-propos`} className="mt-10">
            {t.founder.cta} <ArrowRight size={16} />
          </OutlineLink>
        </Reveal>
      </div>
    </div>
  </section>
);

const FinalCta = ({ lang, t }) => (
  <section data-testid="final-cta-section" className="border-t border-white/10">
    <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-28 md:py-40">
      <Reveal className="text-center">
        <Diamond className="text-sm" />
        <h2 className="mx-auto mt-8 max-w-4xl font-serif text-3xl sm:text-5xl lg:text-6xl font-light leading-tight text-white">
          {t.finalCta.title}
        </h2>
        <div className="mt-12 flex justify-center">
          <GoldLink testId="final-cta-button" to={`/${lang}/bilan`}>
            {t.finalCta.cta} <ArrowRight size={16} />
          </GoldLink>
        </div>
      </Reveal>
    </div>
  </section>
);

const HomePage = () => {
  const { lang, t } = useLang();
  useEffect(() => {
    document.title = t.pageTitles.home;
    document.documentElement.lang = lang;
    const m = document.querySelector('meta[name="description"]');
    if (m) m.setAttribute("content", t.pageDescs.home);
  }, [lang, t]);
  return (
    <>
      <Hero lang={lang} t={t} />
      <Marquee items={t.marquee} />
      <Method t={t} />
      <Pathways lang={lang} t={t} />
      <KitHighlight lang={lang} t={t} />
      <Founder lang={lang} t={t} />
      <FinalCta lang={lang} t={t} />
    </>
  );
};

export default HomePage;
